
import React, { useState, useEffect } from 'react';
import { X, Calendar, CheckSquare, MessageSquare, Plus, Trash2, Send, Clock, User, Home, Phone, Mail, Building, Wallet, AlertCircle, CheckCircle2, XCircle, ArrowRight, ChevronRight, ThumbsUp, ThumbsDown, MessageCircle, Bath, BedDouble, Users, MapPin, Zap, Play, SkipForward, FileText, Key, RefreshCw, Ban, Edit2, PenTool, Globe, Briefcase, Download, Bell } from 'lucide-react';
import { Deal, Owner, Property, Note, Reminder, Agency, DealStage, SequenceStep, StepType, StepStatus, SequenceTemplate, UserProfile, PipelineType, Contract, MarketingLead, MarketingLeadNote, MarketingLeadDocument } from '../../types';
import { storage } from '../../services/storage';
import { useModal } from '../ui/ModalProvider';
import { formatPhoneForStorage, formatPhoneForDisplay, handlePhoneInputChange } from '../../lib/phoneUtils';
import { SearchableSelect } from '../ui/SearchableSelect';

interface DealDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  deal: Deal;
  owner: Owner;
  property: Property | null; // Peut être null si le deal n'a plus de property_id
  agency?: Agency;
  userRole?: 'admin' | 'user';
  userProfile?: UserProfile | null;
  onUpdate: (deal: Deal) => void;
  onUpdateOwner?: (owner: Owner) => Promise<void>;
  onUpdateProperty?: (property: Property) => Promise<void>;
  onDelete?: () => void;
  initialTab?: 'notes' | 'reminders' | 'sequence';
  onGenerateProposal?: () => void;
  onNavigateToContract?: () => void; // New Prop
  onNavigateToManaged?: (propertyId: string) => void;
}

export const DealDetailModal: React.FC<DealDetailModalProps> = ({ isOpen, onClose, deal, owner, property, agency, userRole = 'user', userProfile, onUpdate, onUpdateOwner, onUpdateProperty, onDelete, initialTab = 'notes', onGenerateProposal, onNavigateToContract, onNavigateToManaged }) => {
  const { confirm, prompt, toast } = useModal();
  const [activeTab, setActiveTab] = useState<'notes' | 'reminders' | 'sequence'>(initialTab);
  const [newNote, setNewNote] = useState('');
  const [newReminderDate, setNewReminderDate] = useState('');
  const [newReminderTime, setNewReminderTime] = useState('');
  const [newReminderText, setNewReminderText] = useState('');
  
  // Fonction helper pour afficher le nom d'auteur (affiche "Moi" si c'est l'utilisateur connecté)
  const displayAuthorName = (noteAuthorName: string): string => {
    if (!userProfile) return noteAuthorName;
    const currentUserName = userProfile.firstName && userProfile.lastName
      ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
      : userProfile.email?.split('@')[0] || '';
    return noteAuthorName === currentUserName ? 'Moi' : noteAuthorName;
  };
  
  const [isEditingValue, setIsEditingValue] = useState(false);
  const [editValue, setEditValue] = useState(deal.value?.toString() || '0');
  const [isEditingRent, setIsEditingRent] = useState(false);
  const [editRent, setEditRent] = useState(deal.monthlyRentWithCharges?.toString() || '');
  const [isEditingType, setIsEditingType] = useState(false);
  
  // États pour l'édition du propriétaire
  const [isEditingOwner, setIsEditingOwner] = useState(false);
  const [editOwner, setEditOwner] = useState<Owner>(owner);
  
  // États pour l'édition du bien
  const [isEditingProperty, setIsEditingProperty] = useState(false);
  const [editProperty, setEditProperty] = useState<Property | null>(property);
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  
  // Mettre à jour editProperty quand property change
  useEffect(() => {
    setEditProperty(property);
  }, [property]);

  // Dynamic Sequences State
  const [templates, setTemplates] = useState<SequenceTemplate[]>([]);

  // Marketing Lead State
  const [marketingLead, setMarketingLead] = useState<MarketingLead | null>(null);
  const [loadingMarketingLead, setLoadingMarketingLead] = useState(false);

  // Agencies State (for assignment)
  const [allAgencies, setAllAgencies] = useState<Agency[]>([]);

  // Réinitialiser l'onglet uniquement lors de l'ouverture de la modale
  const prevIsOpen = React.useRef(isOpen);
  
  useEffect(() => {
      if (isOpen) {
          // Ne réinitialiser l'onglet que si la modale vient de s'ouvrir (passage de false à true)
          if (!prevIsOpen.current) {
              setActiveTab(initialTab);
          }
          storage.getSequenceTemplates().then(setTemplates);
          setEditValue(deal.value?.toString() || '0');
          setEditRent(deal.monthlyRentWithCharges?.toString() || '');
          setEditOwner({ ...owner, phone: formatPhoneForDisplay(owner.phone || ''), newsletterOptin: owner.newsletterOptin || false });
          setEditProperty(property);
          setAddressSuggestions([]);
          
          // Charger toutes les agences (pour l'assignation)
          if (userProfile?.role === 'admin' || userProfile?.role === 'superadmin') {
            storage.getAllAgencies().then(setAllAgencies);
          }
          
          // Charger le lead marketing si le deal en provient
          if (deal.marketingLeadId) {
              setLoadingMarketingLead(true);
              storage.getMarketingLead(deal.marketingLeadId)
                  .then((lead) => {
                      setMarketingLead(lead);
                      setLoadingMarketingLead(false);
                  })
                  .catch((error) => {
                      console.error('Erreur lors du chargement du lead marketing:', error);
                      setLoadingMarketingLead(false);
                  });
          } else {
              setMarketingLead(null);
          }
      } else {
          setMarketingLead(null);
      }
      prevIsOpen.current = isOpen;
  }, [isOpen, initialTab, deal.value, deal.monthlyRentWithCharges, deal.marketingLeadId, owner, property]);
  
  // Fonction pour la recherche d'adresse (API GOUV)
  const handleAddressSearch = async (query: string) => {
      setEditProperty({ ...editProperty, address: query });
      if (query.length > 3) {
          try {
              const res = await fetch(`https://data.geopf.fr/geocodage/search/?q=${encodeURIComponent(query)}&limit=5`);
              const data = await res.json();
              setAddressSuggestions(data.features || []);
          } catch (e) {
              console.error(e);
          }
      } else {
          setAddressSuggestions([]);
      }
  };
  
  const selectAddress = (feature: any) => {
      setEditProperty({
          ...editProperty,
          address: feature.properties.name,
          city: feature.properties.city,
          zipCode: feature.properties.postcode
      });
      setAddressSuggestions([]);
  };

  if (!isOpen) return null;

  // Actions logic
  const handleStageChange = (newStage: DealStage) => onUpdate({ ...deal, stage: newStage });
  
  const handleWon = async () => {
      const note: Note = { id: Date.now().toString(), content: "🎉 Deal marqué comme GAGNÉ ! Le bien est passé en gestion.", createdAt: new Date().toISOString(), authorName: 'Système' };
      
      // NEW: Cancel pending steps
      const updatedSteps = (deal.sequenceSteps || []).map(step => 
        step.status === 'PENDING' ? { ...step, status: 'CANCELLED' as const } : step
      );

      // 1. Update Deal locally and via prop
      const updatedDeal = { 
          ...deal, 
          stage: DealStage.SIGNED, 
          notes: [...(deal.notes || []), note],
          sequenceSteps: updatedSteps
      };
      onUpdate(updatedDeal);

      // 2. Update Property Status to MANAGED via Storage
      if (!property || property.status !== 'MANAGED' || !property.contractType) {
          await storage.saveProperty({ 
              ...property, 
              status: 'MANAGED',
              contractType: deal.type // Propagate Conciergerie or Sublet type
          });
      }

      // 3. Sync Proposal Status to ACCEPTED if exists
      if (deal.proposalId) {
          const proposals = await storage.getProposals();
          const linkedProposal = proposals.find(p => p.id === deal.proposalId);
          if (linkedProposal) {
              await storage.saveProposal({
                  ...linkedProposal,
                  status: 'ACCEPTED'
              });
          }
      }

      // 4. Sauvegarder le deal en base pour s'assurer que marketingProviderId est à jour
      const savedDealResult = await storage.saveDeal(updatedDeal);
      const savedDeal = savedDealResult.data || updatedDeal;

      // 5. Envoyer notification au prestataire marketing si applicable (même sans contrat signé)
      if (savedDeal.marketingProviderId) {
          try {
              console.log('[DealDetailModal] Sending notification to marketing provider', {
                  marketingProviderId: savedDeal.marketingProviderId,
                  dealId: savedDeal.id
              });

              const provider = await storage.getMarketingProvider(savedDeal.marketingProviderId);
              if (!provider) {
                  console.warn('[DealDetailModal] Marketing provider not found', savedDeal.marketingProviderId);
              } else if (!provider.email) {
                  console.warn('[DealDetailModal] Marketing provider has no email', provider);
              } else {

              // Récupérer le contrat s'il existe, sinon créer un contrat minimal pour la notification
              let contract: Contract | null = null;
              if (savedDeal.contractId) {
                  const contracts = await storage.getContracts();
                  contract = contracts.find(c => c.id === savedDeal.contractId) || null;
              }
              
              // Si pas de contrat, créer un contrat minimal pour la notification
              if (!contract) {
                  contract = {
                      id: `temp-${Date.now()}`,
                      dealId: savedDeal.id || '',
                      proposalId: savedDeal.proposalId,
                      type: savedDeal.type,
                      status: 'SIGNED',
                      startDate: new Date().toISOString().split('T')[0],
                      duration: '12 mois',
                      marketingProviderId: savedDeal.marketingProviderId,
                      signedAt: new Date().toISOString()
                  };
              }

              // Récupérer l'agence
              const agencies = await storage.getAllAgencies();
              const agency = agencies.find(a => a.id === savedDeal.agencyId);
              const agencyName = agency ? (agency.name || agency.city) : 'Agence inconnue';
              
              console.log('[DealDetailModal] Sending email to provider', {
                  providerEmail: provider.email,
                  agencyName,
                  ownerName: `${owner.firstName} ${owner.lastName}`
              });

              // Envoyer l'email
              const { emailService } = await import('../../services/email');
              await emailService.sendContractSignedNotification(
                  provider.email,
                  contract,
                  savedDeal,
                  owner,
                  agencyName
              );

                  console.log('[DealDetailModal] Email sent successfully to marketing provider');
              }
          } catch (emailError) {
              console.error('[DealDetailModal] Error sending contract signed notification to marketing provider', emailError);
              // Ne pas bloquer le processus si l'email échoue
          }
      } else {
          console.log('[DealDetailModal] No marketingProviderId on deal, skipping notification', {
              dealId: savedDeal.id,
              marketingProviderId: savedDeal.marketingProviderId
          });
      }

      toast.success("Félicitations ! Le deal est gagné.");
  };

  const handleLost = async () => {
      const reason = await prompt({
          title: "Deal Perdu",
          message: "Quel est le motif de la perte ?",
          placeholder: "Ex: Prix trop élevé, concurrence..."
      });
      
      if (reason !== null) {
          let notes = deal.notes || [];
          notes = [...notes, { id: Date.now().toString(), content: `❌ Deal PERDU. Motif : ${reason}`, createdAt: new Date().toISOString(), authorName: 'Système' }];
          onUpdate({ ...deal, stage: DealStage.LOST, notes: notes });
          toast.info("Deal marqué comme perdu.");
      }
  };

  const handleReopen = async () => {
      if (deal.stage === DealStage.SIGNED) {
          if (!await confirm({
              title: "Rouvrir le dossier ?",
              message: "Attention : Rouvrir un dossier gagné repassera le bien en 'Prospect' et la proposition en 'Envoyée'. Continuer ?",
              confirmText: "Oui, rouvrir",
              danger: true
          })) return;
          
          // 1. Revert Property to PROSPECT (Force update regardless of current state)
          await storage.saveProperty({ 
              ...property, 
              status: 'PROSPECT',
              // Keep other managed fields or reset them? Keeping them allows re-winning without data loss.
          });

          // 2. Revert Proposal to SENT
          if (deal.proposalId) {
              const proposals = await storage.getProposals();
              const linkedProposal = proposals.find(p => p.id === deal.proposalId);
              if (linkedProposal) {
                  await storage.saveProposal({ ...linkedProposal, status: 'SENT' });
              }
          }
      }

      const note: Note = { 
          id: Date.now().toString(), 
          content: `🔄 Dossier rouvert (Ancien statut: ${deal.stage})`, 
          createdAt: new Date().toISOString(), 
          authorName: 'Système' 
      };
      
      onUpdate({ ...deal, stage: DealStage.QUALIFIED, notes: [...(deal.notes || []), note] });
      toast.info("Dossier rouvert.");
  };

  const handleAddNote = () => {
      if (!newNote.trim()) return;
      
      // Construire le nom complet de l'auteur
      const authorName = userProfile 
        ? (userProfile.firstName && userProfile.lastName 
            ? `${userProfile.firstName} ${userProfile.lastName}`.trim()
            : userProfile.email?.split('@')[0] || 'Utilisateur')
        : 'Utilisateur';
      
      const note: Note = { id: Date.now().toString(), content: newNote, createdAt: new Date().toISOString(), authorName };
      onUpdate({ ...deal, notes: [...(deal.notes || []), note] });
      setNewNote('');
  };

  const handleAddReminder = () => {
      if (!newReminderText.trim() || !newReminderDate) return;
      const hasTime = !!newReminderTime;
      // Si une heure est saisie => RDV (event), sinon => tâche (Google Tasks)
      const localIso = hasTime
        ? `${newReminderDate}T${newReminderTime}:00`
        : `${newReminderDate}T00:00:00`;
      const dateObj = new Date(localIso);
      const nowIso = new Date().toISOString();

      const reminder: Reminder = {
          id: Date.now().toString(),
          date: dateObj.toISOString(),
          note: newReminderText,
          isDone: false,
          time: hasTime ? newReminderTime : undefined,
          startAt: hasTime ? dateObj.toISOString() : undefined,
          endAt: hasTime ? new Date(dateObj.getTime() + 60 * 60 * 1000).toISOString() : undefined,
          updatedAt: nowIso,
      };
      onUpdate({ ...deal, reminders: [...(deal.reminders || []), reminder] });
      setNewReminderText('');
      setNewReminderDate('');
      setNewReminderTime('');
      toast.success("Rappel ajouté.");
  };

  const toggleReminder = (id: string) => onUpdate({ ...deal, reminders: deal.reminders?.map(r => r.id === id ? { ...r, isDone: !r.isDone } : r) });
  const deleteReminder = (id: string) => onUpdate({ ...deal, reminders: deal.reminders?.filter(r => r.id !== id) });
  const saveValue = () => { onUpdate({ ...deal, value: parseFloat(editValue) }); setIsEditingValue(false); };
  const saveRent = () => {
    const rentValue = editRent ? parseFloat(editRent) : undefined;
    onUpdate({ ...deal, monthlyRentWithCharges: rentValue });
    setIsEditingRent(false);
  };

  // Fonction pour changer le type de deal
  const handleTypeChange = async (newType: PipelineType) => {
    // Validation : bloquer si proposition ou contrat existe
    if (deal.proposalId) {
      toast.error("Impossible de changer le type : une proposition est déjà liée à ce deal.");
      setIsEditingType(false);
      return;
    }
    if (deal.contractId) {
      toast.error("Impossible de changer le type : un contrat est déjà lié à ce deal.");
      setIsEditingType(false);
      return;
    }

    const oldType = deal.type;
    const oldTypeLabel = oldType === PipelineType.CONCIERGE ? 'Conciergerie' : 'Sous-location';
    const newTypeLabel = newType === PipelineType.CONCIERGE ? 'Conciergerie' : 'Sous-location';

    // Préparer les modifications
    let updatedDeal: Deal = { ...deal, type: newType };

    // Si passage de Sous-location → Conciergerie : effacer monthlyRentWithCharges
    if (oldType === PipelineType.SUBLET && newType === PipelineType.CONCIERGE) {
      updatedDeal.monthlyRentWithCharges = undefined;
    }
    // Si passage de Conciergerie → Sous-location : conserver monthlyRentWithCharges s'il existe

    // Ajouter une note automatique dans l'historique
    const note: Note = {
      id: Date.now().toString(),
      content: `🔄 Type changé : ${oldTypeLabel} → ${newTypeLabel}`,
      createdAt: new Date().toISOString(),
      authorName: 'Système'
    };
    updatedDeal.notes = [...(deal.notes || []), note];

    // Mettre à jour le deal
    onUpdate(updatedDeal);

    // Si deal gagné et property existe : mettre à jour property.contractType
    if (deal.stage === DealStage.SIGNED && property && onUpdateProperty) {
      try {
        await onUpdateProperty({
          ...property,
          contractType: newType
        });
      } catch (error) {
        console.error('Erreur lors de la mise à jour du contractType du bien:', error);
      }
    }

    setIsEditingType(false);
    toast.success(`Type changé : ${oldTypeLabel} → ${newTypeLabel}`);
  };
  
  const saveOwner = async () => {
    if (onUpdateOwner) {
      try {
        const ownerToSave = {
          ...editOwner,
          phone: editOwner.phone ? formatPhoneForStorage(editOwner.phone) : ''
        };
        await onUpdateOwner(ownerToSave);
        setIsEditingOwner(false);
        toast.success("Propriétaire mis à jour");
        // Mettre à jour le deal si needsCompletion était true et que les données sont maintenant complètes
        if (deal.needsCompletion) {
          const ownerComplete = editOwner.firstName && editOwner.firstName !== 'A définir' && editOwner.email && editOwner.phone;
          const propertyComplete = editProperty && editProperty.address && editProperty.surface && editProperty.surface > 0;
          if (ownerComplete && propertyComplete) {
            onUpdate({ ...deal, needsCompletion: false });
            toast.success("Toutes les données sont maintenant complètes !");
          }
        }
      } catch (error) {
        toast.error("Erreur lors de la mise à jour du propriétaire");
      }
    } else {
      setIsEditingOwner(false);
    }
  };
  
  const saveProperty = async () => {
    if (onUpdateProperty) {
      try {
        await onUpdateProperty(editProperty);
        setIsEditingProperty(false);
        toast.success("Bien mis à jour");
        // Mettre à jour le deal si needsCompletion était true et que les données sont maintenant complètes
        if (deal.needsCompletion) {
          const ownerComplete = owner.firstName && owner.firstName !== 'A définir' && owner.email && owner.phone;
          const propertyComplete = editProperty.address && editProperty.surface && editProperty.surface > 0;
          if (ownerComplete && propertyComplete) {
            onUpdate({ ...deal, needsCompletion: false });
            toast.success("Toutes les données sont maintenant complètes !");
          }
        }
      } catch (error) {
        toast.error("Erreur lors de la mise à jour du bien");
      }
    } else {
      setIsEditingProperty(false);
    }
  };

  // --- SEQUENCE LOGIC ---
  const startSequence = (template: SequenceTemplate) => {
      const today = new Date();
      
      const newSteps: SequenceStep[] = template.steps.map((step, index) => {
          const stepDate = new Date(today);
          stepDate.setDate(today.getDate() + step.dayOffset);
          
          return {
              id: Date.now().toString() + index,
              type: step.type as StepType,
              dayOffset: step.dayOffset,
              date: stepDate.toISOString(),
              title: step.title,
              content: step.content,
              status: 'PENDING'
          };
      });

      // Add a note
      const note: Note = { id: Date.now().toString(), content: `🚀 Séquence "${template.name}" démarrée`, createdAt: new Date().toISOString(), authorName: 'Système' };
      
      onUpdate({ 
          ...deal, 
          sequenceSteps: newSteps, 
          notes: [...(deal.notes || []), note] 
      });
      setActiveTab('sequence');
      toast.success("Séquence démarrée !");
  };

  const executeStep = (step: SequenceStep) => {
      const userName = userProfile ? `${userProfile.firstName} ${userProfile.lastName}` : 'L\'équipe Placid';

      // 1. Prepare Content (Replacements)
      let body = step.content
        .replace(/\[PRENOM\]/g, owner.firstName)
        .replace(/\[NOM\]/g, owner.lastName)
        .replace(/\[ADRESSE\]/g, property?.address || '')
        .replace(/\[VILLE\]/g, property?.city || '')
        .replace(/\[VALEUR\]/g, deal.value.toString())
        .replace(/\[NOM_USER\]/g, userName);

      // 2. Open External App
      if (step.type === 'EMAIL') {
          const subject = encodeURIComponent(`Concernant votre bien à ${property?.city || ''}`);
          window.open(`mailto:${owner.email}?subject=${subject}&body=${encodeURIComponent(body)}`);
      } else if (step.type === 'WHATSAPP') {
          let phone = formatPhoneForStorage(owner.phone).replace(/[^\d]/g, ''); // Clean
          window.open(`https://wa.me/${phone}?text=${encodeURIComponent(body)}`);
      } else if (step.type === 'SMS') {
          const phone = formatPhoneForStorage(owner.phone);
          window.open(`sms:${phone}?body=${encodeURIComponent(body)}`);
      } else if (step.type === 'CALL') {
          const phone = formatPhoneForStorage(owner.phone);
          window.open(`tel:${phone}`);
      }

      // 3. Mark as Sent
      const updatedSteps = deal.sequenceSteps?.map(s => s.id === step.id ? { ...s, status: 'SENT' as StepStatus } : s);
      onUpdate({ ...deal, sequenceSteps: updatedSteps });
  };

  const skipStep = (stepId: string) => {
      const updatedSteps = deal.sequenceSteps?.map(s => s.id === stepId ? { ...s, status: 'SKIPPED' as StepStatus } : s);
      onUpdate({ ...deal, sequenceSteps: updatedSteps });
  };
  
  const resetSequence = async () => {
      if(await confirm({
          title: "Arrêter la séquence",
          message: "Voulez-vous vraiment arrêter la séquence en cours ? Les tâches prévues seront supprimées.",
          danger: true
      })) {
        onUpdate({ ...deal, sequenceSteps: [] });
        toast.info("Séquence arrêtée.");
      }
  };


  const cleanPhone = (phone: string) => {
      const formatted = formatPhoneForStorage(phone);
      return formatted.replace(/[^\d]/g, '').replace(/^33/, '');
  };

  const sortedNotes = [...(deal.notes || [])].sort((a,b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const sortedReminders = [...(deal.reminders || [])].sort((a,b) => {
      if (a.isDone === b.isDone) return new Date(a.date).getTime() - new Date(b.date).getTime();
      return a.isDone ? 1 : -1;
  });
  const sequenceSteps = deal.sequenceSteps || [];
  const hasCancelledSteps = sequenceSteps.some(s => s.status === 'CANCELLED');

  const stages = Object.values(DealStage).filter(s => s !== DealStage.SIGNED && s !== DealStage.LOST);
  const currentStageIndex = stages.indexOf(deal.stage as any);
  const isClosed = deal.stage === DealStage.SIGNED || deal.stage === DealStage.LOST;

  // Vérifier si les données sont incomplètes
  const isOwnerIncomplete = deal.needsCompletion && (
    !editOwner.firstName || 
    editOwner.firstName === 'A définir' || 
    !editOwner.email || 
    editOwner.email.trim() === '' || 
    !editOwner.phone || 
    editOwner.phone.trim() === ''
  );
  
  // Pour le bien, toujours afficher "à compléter" si needsCompletion est true
  // car même avec des données, l'adresse LBC n'est jamais complète
  const isPropertyIncomplete = deal.needsCompletion;

  // --- RENDER ACTION BUTTONS ---
  const hasProposal = !!deal.proposalId;
  const hasContract = !!deal.contractId;
  const canGenerateContract = !hasContract && (deal.stage === DealStage.PROPOSAL_ACCEPTED || deal.stage === DealStage.CONTRACT_SENT);
  const canChangeType = !hasProposal && !hasContract;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white h-full w-full max-w-6xl shadow-2xl flex flex-col rounded-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        
        {/* HEADER */}
        <div className="p-6 border-b border-slate-100 bg-white shrink-0 z-10 flex flex-col gap-4">
            <div className="flex justify-between items-start">
                 <div className="flex items-center gap-4">
                    {property ? (
                        <>
                            <div className="p-3 bg-slate-100 rounded-xl">
                                {property.type === 'Maison' ? <Home className="w-6 h-6 text-slate-700" /> : <Building className="w-6 h-6 text-slate-700" />}
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 leading-tight">{property.type} {property.city}</h2>
                                <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                                    {deal.type === PipelineType.CONCIERGE ? (
                                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border flex items-center uppercase tracking-tighter bg-blue-50 text-blue-700 border-blue-200">
                                            <Key className="w-2.5 h-2.5 mr-1" />
                                            CONCIERGE
                                        </span>
                                    ) : (
                                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border flex items-center uppercase tracking-tighter bg-purple-50 text-purple-700 border-purple-200">
                                            <Briefcase className="w-2.5 h-2.5 mr-1" />
                                            SOUS-LOC
                                        </span>
                                    )}
                                    {agency && <span className="uppercase font-bold tracking-wide text-xs bg-slate-100 px-2 py-0.5 rounded">{agency.city}</span>}
                                    <span>Créé le {new Date(deal.createdAt || '').toLocaleDateString()}</span>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="p-3 bg-amber-100 rounded-xl">
                                <AlertCircle className="w-6 h-6 text-amber-700" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-900 leading-tight">Bien non lié</h2>
                                <p className="text-sm text-slate-500 mt-1">Ce deal n'est plus associé à un bien</p>
                                <div className="flex items-center gap-2 text-sm text-slate-500 mt-1">
                                    {deal.type === PipelineType.CONCIERGE ? (
                                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border flex items-center uppercase tracking-tighter bg-blue-50 text-blue-700 border-blue-200">
                                            <Key className="w-2.5 h-2.5 mr-1" />
                                            CONCIERGE
                                        </span>
                                    ) : (
                                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border flex items-center uppercase tracking-tighter bg-purple-50 text-purple-700 border-purple-200">
                                            <Briefcase className="w-2.5 h-2.5 mr-1" />
                                            SOUS-LOC
                                        </span>
                                    )}
                                    {agency && <span className="uppercase font-bold tracking-wide text-xs bg-slate-100 px-2 py-0.5 rounded">{agency.city}</span>}
                                    <span>Créé le {new Date(deal.createdAt || '').toLocaleDateString()}</span>
                                </div>
                            </div>
                        </>
                    )}
                 </div>
                 
                 <div className="flex items-center space-x-2">
                    {userRole === 'admin' && onDelete && (
                        <button onClick={onDelete} className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors" title="Supprimer">
                            <Trash2 className="w-5 h-5" />
                        </button>
                    )}
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                        <X className="w-6 h-6 text-slate-500" />
                    </button>
                 </div>
            </div>

            {/* ALERTE DONNÉES INCOMPLÈTES (LBC) */}
            {deal.needsCompletion && deal.source === 'Leboncoin' && (
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-4 shadow-sm">
                    <div className="flex items-start gap-3">
                        <div className="bg-amber-100 p-2 rounded-lg">
                            <AlertCircle className="w-5 h-5 text-amber-600" />
                        </div>
                        <div className="flex-1">
                            <h5 className="text-sm font-bold text-amber-900 mb-1 flex items-center gap-2">
                                <Globe className="w-4 h-4" />
                                Données incomplètes depuis Leboncoin
                            </h5>
                            <p className="text-sm text-amber-800 mb-2">
                                Ce deal a été créé depuis Leboncoin avec des données incomplètes. Veuillez vérifier et compléter les informations du propriétaire et du bien.
                            </p>
                            <p className="text-xs text-amber-600 italic">
                                💡 Vérifiez notamment : nom/prénom du propriétaire, email, téléphone, et adresse complète du bien.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* STAGE STEPPER */}
            {!isClosed ? (
                <div className="w-full bg-slate-50 p-2 rounded-xl flex items-center justify-between">
                    {stages.map((stage, idx) => {
                        const isPast = idx <= currentStageIndex;
                        const isCurrent = idx === currentStageIndex;
                        return (
                            <div key={stage} className="flex flex-col items-center flex-1 cursor-pointer group relative" onClick={() => handleStageChange(stage)}>
                                <div className={`flex items-center w-full ${idx === 0 ? 'justify-start' : idx === stages.length -1 ? 'justify-end' : 'justify-center'}`}>
                                    <div className={`h-2 w-full rounded-full transition-all mx-1 ${isCurrent ? 'bg-amber-500 ring-2 ring-amber-200' : isPast ? 'bg-amber-300' : 'bg-slate-200 group-hover:bg-slate-300'}`}></div>
                                </div>
                                <span className={`text-xs font-bold mt-2 text-center truncate w-full px-1 ${isCurrent ? 'text-amber-600' : 'text-slate-400'}`}>{stage}</span>
                            </div>
                        )
                    })}
                </div>
            ) : (
                <div className={`p-4 rounded-xl flex items-center justify-between ${deal.stage === DealStage.SIGNED ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    <div className="flex items-center font-bold text-lg">
                        {deal.stage === DealStage.SIGNED ? <ThumbsUp className="w-6 h-6 mr-3" /> : <ThumbsDown className="w-6 h-6 mr-3" />}
                        DEAL {deal.stage === DealStage.SIGNED ? 'GAGNÉ' : 'PERDU'}
                    </div>
                    
                    <div className="flex items-center space-x-3">
                        <button 
                            onClick={handleReopen} 
                            className="text-sm bg-white/50 hover:bg-white px-4 py-2 rounded-lg flex items-center font-medium transition-colors border border-black/5 hover:border-black/20 text-current"
                            title="Rouvrir le dossier pour modification"
                        >
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Rouvrir le dossier
                        </button>
                    </div>
                </div>
            )}
        </div>

        <div className="flex-1 overflow-hidden grid grid-cols-12 bg-slate-50/50">
            
            {/* LEFT SIDEBAR - CONTEXT */}
            <div className="col-span-4 border-r border-slate-200 bg-white overflow-y-auto p-6 space-y-6">
                
                {/* ACTION BUTTONS GRID */}
                <div className="grid grid-cols-2 gap-3">
                    {!isClosed && (
                        <>
                            <button onClick={handleWon} className="flex items-center justify-center p-3 bg-green-50 hover:bg-green-100 border border-green-200 hover:border-green-300 rounded-xl text-green-800 transition-all font-bold group">
                                <CheckCircle2 className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                                GAGNÉ
                            </button>
                            <button onClick={handleLost} className="flex items-center justify-center p-3 bg-red-50 hover:bg-red-100 border border-red-200 hover:border-red-300 rounded-xl text-red-800 transition-all font-bold group">
                                <XCircle className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                                PERDU
                            </button>
                        </>
                    )}
                    
                    {/* MANAGED PROPERTY (WON ONLY) */}
                    {deal.stage === DealStage.SIGNED && (
                        <button 
                            onClick={() => onNavigateToManaged && deal.propertyId && onNavigateToManaged(deal.propertyId)}
                            disabled={!deal.propertyId}
                            className={`col-span-2 flex items-center justify-center p-3 border rounded-xl transition-all font-bold group ${
                                !deal.propertyId
                                    ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                                    : 'bg-green-50 hover:bg-green-100 border-green-200 hover:border-green-300 text-green-800'
                            }`}
                        >
                            <Key className={`w-5 h-5 mr-2 ${!deal.propertyId ? '' : 'group-hover:scale-110 transition-transform'}`} />
                            VOIR LE BIEN GÉRÉ
                        </button>
                    )}

                    {/* CONTRACT ACTIONS */}
                    {hasContract ? (
                        <button 
                            onClick={onNavigateToContract} 
                            className={`${hasProposal ? 'col-span-1' : 'col-span-2'} flex items-center justify-center p-3 bg-purple-50 hover:bg-purple-100 border border-purple-200 hover:border-purple-300 rounded-xl text-purple-800 transition-all font-bold group`}
                        >
                            <PenTool className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                            VOIR CONTRAT
                        </button>
                    ) : (
                        canGenerateContract && (
                            <button 
                                onClick={onNavigateToContract} 
                                className={`${hasProposal ? 'col-span-1' : 'col-span-2'} flex items-center justify-center p-3 bg-purple-50 hover:bg-purple-100 border border-purple-200 hover:border-purple-300 rounded-xl text-purple-800 transition-all font-bold group`}
                            >
                                <PenTool className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                                GÉNÉRER CONTRAT
                            </button>
                        )
                    )}

                    {/* PROPOSAL ACTIONS */}
                    {hasProposal ? (
                        <button 
                            onClick={onGenerateProposal} 
                            className={`${hasContract || canGenerateContract ? 'col-span-1' : 'col-span-2'} flex items-center justify-center p-3 bg-amber-50 hover:bg-amber-100 border border-amber-200 hover:border-amber-300 rounded-xl text-amber-800 transition-all font-bold group`}
                        >
                            <FileText className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                            VOIR PROPO.
                        </button>
                    ) : (
                        !isClosed && deal.type === PipelineType.CONCIERGE && (
                            <button 
                                onClick={onGenerateProposal} 
                                className="col-span-2 flex items-center justify-center p-3 bg-amber-50 hover:bg-amber-100 border border-amber-200 hover:border-amber-300 rounded-xl text-amber-800 transition-all font-bold group"
                            >
                                <FileText className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                                CRÉER UNE PROPOSITION
                            </button>
                        )
                    )}
                </div>

                {/* 1. FINANCIAL */}
                <div className="bg-emerald-50 rounded-xl border border-emerald-100 p-5">
                    <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center text-emerald-800 font-bold">
                            <Wallet className="w-5 h-5 mr-2" />
                            Revenu Estimé
                        </div>
                        <span className="text-xs text-emerald-600 font-medium bg-white px-2 py-0.5 rounded-full border border-emerald-100">Mensuel</span>
                    </div>
                    
                    {isEditingValue ? (
                        <div className="flex items-center space-x-2 animate-in fade-in mt-2">
                            <input 
                                type="number" 
                                className="w-full p-2 text-2xl font-bold text-emerald-800 border-b-2 border-emerald-500 bg-transparent outline-none"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onFocus={(e) => e.target.select()}
                                autoFocus
                                onBlur={saveValue}
                                onKeyDown={(e) => e.key === 'Enter' && saveValue()}
                            />
                            <span className="text-emerald-800 font-bold text-xl">€</span>
                        </div>
                    ) : (
                        <div className="text-3xl font-bold text-emerald-700 cursor-pointer hover:underline decoration-dashed underline-offset-4 mt-1" onClick={() => setIsEditingValue(true)}>
                            {deal.value?.toLocaleString()} €
                        </div>
                    )}
                </div>

                {/* 1b. RENT (Sous-location uniquement) */}
                {deal.type === PipelineType.SUBLET && (
                    <div className="bg-purple-50 rounded-xl border border-purple-100 p-5">
                        <div className="flex justify-between items-center mb-2">
                            <div className="flex items-center text-purple-800 font-bold">
                                <Wallet className="w-5 h-5 mr-2" />
                                Loyer mensuel charges comprises
                            </div>
                        </div>
                        
                        {isEditingRent ? (
                            <div className="flex items-center space-x-2 animate-in fade-in mt-2">
                                <input 
                                    type="number" 
                                    className="w-full p-2 text-2xl font-bold text-purple-800 border-b-2 border-purple-500 bg-transparent outline-none"
                                    value={editRent}
                                    onChange={(e) => setEditRent(e.target.value)}
                                    onFocus={(e) => e.target.select()}
                                    autoFocus
                                    onBlur={saveRent}
                                    onKeyDown={(e) => e.key === 'Enter' && saveRent()}
                                    placeholder="0"
                                />
                                <span className="text-purple-800 font-bold text-xl">€</span>
                            </div>
                        ) : (
                            <div className="text-3xl font-bold text-purple-700 cursor-pointer hover:underline decoration-dashed underline-offset-4 mt-1" onClick={() => setIsEditingRent(true)}>
                                {deal.monthlyRentWithCharges ? `${deal.monthlyRentWithCharges.toLocaleString()} €` : 'Non renseigné'}
                            </div>
                        )}
                    </div>
                )}

                {/* 1c. TYPE SELECTOR */}
                <div className={`rounded-xl border p-5 ${deal.type === PipelineType.CONCIERGE ? 'bg-blue-50 border-blue-100' : 'bg-purple-50 border-purple-100'}`}>
                    <div className="flex justify-between items-center mb-2">
                        <div className={`flex items-center font-bold ${deal.type === PipelineType.CONCIERGE ? 'text-blue-800' : 'text-purple-800'}`}>
                            {deal.type === PipelineType.CONCIERGE ? <Key className="w-5 h-5 mr-2" /> : <Briefcase className="w-5 h-5 mr-2" />}
                            Type de deal
                        </div>
                        {!isEditingType && canChangeType && (
                            <button
                                onClick={() => setIsEditingType(true)}
                                className="p-1.5 text-slate-400 hover:text-amber-500 transition-colors"
                                title="Modifier le type"
                            >
                                <Edit2 className="w-3.5 h-3.5" />
                            </button>
                        )}
                    </div>
                    
                    {isEditingType ? (
                        <div className="space-y-3 animate-in fade-in mt-2">
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    onClick={() => handleTypeChange(PipelineType.CONCIERGE)}
                                    className={`cursor-pointer p-4 rounded-xl border-2 flex flex-col items-center transition-all ${
                                        deal.type === PipelineType.CONCIERGE
                                            ? 'border-blue-500 bg-blue-100'
                                            : 'border-slate-200 hover:border-blue-300 bg-white'
                                    }`}
                                >
                                    <Key className={`w-6 h-6 mb-2 ${deal.type === PipelineType.CONCIERGE ? 'text-blue-600' : 'text-slate-400'}`} />
                                    <span className={`font-bold text-sm ${deal.type === PipelineType.CONCIERGE ? 'text-blue-700' : 'text-slate-600'}`}>Conciergerie</span>
                                </button>
                                <button
                                    onClick={() => handleTypeChange(PipelineType.SUBLET)}
                                    className={`cursor-pointer p-4 rounded-xl border-2 flex flex-col items-center transition-all ${
                                        deal.type === PipelineType.SUBLET
                                            ? 'border-purple-500 bg-purple-100'
                                            : 'border-slate-200 hover:border-purple-300 bg-white'
                                    }`}
                                >
                                    <Briefcase className={`w-6 h-6 mb-2 ${deal.type === PipelineType.SUBLET ? 'text-purple-600' : 'text-slate-400'}`} />
                                    <span className={`font-bold text-sm ${deal.type === PipelineType.SUBLET ? 'text-purple-700' : 'text-slate-600'}`}>Sous-Location</span>
                                </button>
                            </div>
                            <button
                                onClick={() => setIsEditingType(false)}
                                className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 py-2 rounded-lg text-xs font-bold transition-colors"
                            >
                                Annuler
                            </button>
                        </div>
                    ) : (
                        <div className="mt-1">
                            {deal.type === PipelineType.CONCIERGE ? (
                                <div className="flex items-center gap-2">
                                    <span className="text-lg font-bold text-blue-700">Conciergerie</span>
                                    {!canChangeType && (
                                        <span className="text-xs text-slate-500 italic">
                                            {hasProposal && '(Proposition liée)'}
                                            {hasContract && '(Contrat lié)'}
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <span className="text-lg font-bold text-purple-700">Sous-location</span>
                                    {!canChangeType && (
                                        <span className="text-xs text-slate-500 italic">
                                            {hasProposal && '(Proposition liée)'}
                                            {hasContract && '(Contrat lié)'}
                                        </span>
                                    )}
                                </div>
                            )}
                            {!canChangeType && (
                                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    Le type ne peut pas être modifié car {hasProposal && 'une proposition'} {hasProposal && hasContract && 'et '} {hasContract && 'un contrat'} {hasProposal || hasContract ? 'sont' : 'est'} déjà lié{hasProposal || hasContract ? 's' : ''} à ce deal.
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* ASSIGNATION AGENCE (si non assigné et admin/superadmin) */}
                {!deal.agencyId && (userProfile?.role === 'admin' || userProfile?.role === 'superadmin') && (
                    <div className="border rounded-xl p-5 shadow-sm bg-white border-amber-200 bg-amber-50">
                        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center mb-4">
                            <Building className="w-4 h-4 mr-2" /> Assigner à une agence
                        </h3>
                        <div className="space-y-3">
                            <SearchableSelect
                                options={allAgencies.map(agency => ({
                                    value: agency.id,
                                    label: agency.name || agency.city || 'Agence sans nom'
                                }))}
                                value={''}
                                onChange={async (agencyId) => {
                                    if (!agencyId) return;
                                    try {
                                        await onUpdate({ ...deal, agencyId });
                                        toast.success('Deal assigné à l\'agence avec succès');
                                    } catch (error: any) {
                                        toast.error('Erreur lors de l\'assignation: ' + (error?.message || 'Erreur inconnue'));
                                    }
                                }}
                                placeholder="Rechercher une agence..."
                                icon={<Building className="w-4 h-4" />}
                                showAllOnOpen={true}
                            />
                            <p className="text-xs text-slate-500 mt-2">
                                Sélectionnez une agence pour assigner ce deal. L'agence recevra une notification.
                            </p>
                        </div>
                    </div>
                )}

                {/* 2. CONTACT CARD */}
                <div className={`border rounded-xl p-5 shadow-sm bg-white ${isOwnerIncomplete ? 'border-2 border-orange-400 bg-orange-50/30' : 'border-slate-200'}`}>
                    {isOwnerIncomplete && (
                        <div className="mb-4 bg-orange-100 border border-orange-300 rounded-lg p-2 text-xs text-orange-700 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                            <span className="font-medium">À compléter</span>
                        </div>
                    )}
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center">
                            <User className="w-4 h-4 mr-2" /> Propriétaire
                        </h3>
                        <div className="flex items-center gap-2">
                            {!isEditingOwner && onUpdateOwner && (
                                <button 
                                    onClick={() => setIsEditingOwner(true)}
                                    className="p-1.5 text-slate-400 hover:text-amber-500 transition-colors"
                                    title="Modifier"
                                >
                                    <Edit2 className="w-3.5 h-3.5" />
                                </button>
                            )}
                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-500 text-xs">
                                {editOwner.firstName?.[0] || '?'}{editOwner.lastName?.[0] || '?'}
                            </div>
                        </div>
                    </div>
                    
                    {isEditingOwner ? (
                        <div className="space-y-3 animate-in fade-in">
                            <div>
                                <label className="text-xs font-bold text-slate-500 mb-1 block">Prénom</label>
                                <input 
                                    type="text"
                                    className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm"
                                    value={editOwner.firstName || ''}
                                    onChange={(e) => setEditOwner({...editOwner, firstName: e.target.value})}
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 mb-1 block">Nom</label>
                                <input 
                                    type="text"
                                    className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm"
                                    value={editOwner.lastName || ''}
                                    onChange={(e) => setEditOwner({...editOwner, lastName: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 mb-1 block">Email</label>
                                <input 
                                    type="email"
                                    className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm"
                                    value={editOwner.email || ''}
                                    onChange={(e) => setEditOwner({...editOwner, email: e.target.value})}
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-slate-500 mb-1 block">Téléphone</label>
                                <input 
                                    type="tel"
                                    placeholder="06 12 34 56 78"
                                    className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm font-mono"
                                    value={editOwner.phone || ''}
                                    onChange={(e) => setEditOwner({...editOwner, phone: handlePhoneInputChange(e.target.value)})}
                                    onBlur={() => {
                                        if (editOwner.phone) {
                                            setEditOwner({...editOwner, phone: formatPhoneForStorage(editOwner.phone)});
                                        }
                                    }}
                                />
                            </div>
                            <div className="flex items-start space-x-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <input
                                    type="checkbox"
                                    id="newsletter-optin-deal"
                                    checked={editOwner.newsletterOptin || false}
                                    onChange={(e) => setEditOwner({...editOwner, newsletterOptin: e.target.checked})}
                                    className="mt-1 w-4 h-4 text-amber-600 border-slate-300 rounded focus:ring-amber-500 focus:ring-2"
                                />
                                <div className="flex-1">
                                    <label htmlFor="newsletter-optin-deal" className="text-xs font-semibold text-slate-900 cursor-pointer flex items-center">
                                        <Bell className="w-3.5 h-3.5 mr-2 text-amber-600" />
                                        S'inscrire à la newsletter Placid
                                    </label>
                                    <p className="text-xs text-slate-600 mt-0.5">
                                        Cochez cette case pour permettre au propriétaire de recevoir la newsletter Placid
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button 
                                    onClick={saveOwner}
                                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white py-2 rounded-lg text-xs font-bold transition-colors"
                                >
                                    Enregistrer
                                </button>
                                <button 
                                    onClick={() => { setIsEditingOwner(false); setEditOwner({ ...owner, phone: formatPhoneForDisplay(owner.phone || ''), newsletterOptin: owner.newsletterOptin || false }); }}
                                    className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-2 rounded-lg text-xs font-bold transition-colors"
                                >
                                    Annuler
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="mb-4">
                                <p className="font-bold text-lg text-slate-900">{editOwner.firstName} {editOwner.lastName}</p>
                                <p className="text-sm text-slate-500">{editOwner.email}</p>
                                {editOwner.phone && (
                                    <p className="text-sm text-slate-600 mt-1">{formatPhoneForDisplay(editOwner.phone)}</p>
                                )}
                            </div>

                            <div className="flex space-x-2">
                                <a href={`tel:${editOwner.phone}`} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-2 rounded-lg flex items-center justify-center text-xs font-bold transition-colors">
                                    <Phone className="w-3.5 h-3.5 mr-2" /> Appel
                                </a>
                                <a href={`mailto:${editOwner.email}`} className="flex-1 bg-blue-50 hover:bg-blue-100 text-blue-700 py-2 rounded-lg flex items-center justify-center text-xs font-bold transition-colors border border-blue-200">
                                    <Mail className="w-3.5 h-3.5 mr-2" /> Mail
                                </a>
                                <a href={`https://wa.me/${cleanPhone(editOwner.phone)}`} target="_blank" rel="noreferrer" className="flex-1 bg-green-50 hover:bg-green-100 text-green-700 py-2 rounded-lg flex items-center justify-center text-xs font-bold transition-colors border border-green-200">
                                    <MessageCircle className="w-3.5 h-3.5 mr-2" /> WhatsApp
                                </a>
                            </div>
                        </>
                    )}
                </div>

                {/* 3. PROPERTY SPECS */}
                <div className={`border rounded-xl p-5 shadow-sm bg-white ${isPropertyIncomplete ? 'border-2 border-orange-400 bg-orange-50/30' : 'border-slate-200'}`}>
                    {!property ? (
                        <div className="text-center py-8">
                            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                            <p className="text-sm font-medium text-slate-700 mb-1">Bien non lié</p>
                            <p className="text-xs text-slate-500">Ce deal n'est plus associé à un bien immobilier</p>
                        </div>
                    ) : (
                        <>
                            {isPropertyIncomplete && (
                                <div className="mb-4 bg-orange-100 border border-orange-300 rounded-lg p-2 text-xs text-orange-700 flex items-center gap-2">
                                    <AlertCircle className="w-4 h-4 text-orange-500 flex-shrink-0" />
                                    <span className="font-medium">À compléter</span>
                                </div>
                            )}
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider flex items-center">
                                    <Home className="w-4 h-4 mr-2" /> Caractéristiques
                                </h3>
                                {!isEditingProperty && onUpdateProperty && (
                                    <button 
                                        onClick={() => setIsEditingProperty(true)}
                                        className="p-1.5 text-slate-400 hover:text-amber-500 transition-colors"
                                        title="Modifier"
                                    >
                                        <Edit2 className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                            
                            {isEditingProperty ? (
                        <div className="space-y-3 animate-in fade-in">
                            <div className="relative">
                                <label className="text-xs font-bold text-slate-500 mb-1 block">Adresse</label>
                                <MapPin className="absolute left-3 top-8 w-4 h-4 text-slate-400" />
                                <input 
                                    type="text"
                                    className="w-full pl-10 p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm"
                                    value={editProperty?.address || ''}
                                    onChange={(e) => handleAddressSearch(e.target.value)}
                                    placeholder="Recherche auto..."
                                    autoFocus
                                />
                                {addressSuggestions.length > 0 && (
                                    <ul className="absolute z-10 w-full bg-white border border-slate-200 rounded-xl mt-2 shadow-xl max-h-48 overflow-auto">
                                        {addressSuggestions.map((s, i) => (
                                            <li key={i} onClick={() => selectAddress(s)} className="p-3 hover:bg-slate-50 cursor-pointer text-sm border-b border-slate-50 last:border-0 text-slate-900">
                                                <span className="font-bold block">{s.properties.name}</span>
                                                <span className="text-slate-500 text-xs">{s.properties.postcode} {s.properties.city}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-xs font-bold text-slate-500 mb-1 block">Code Postal</label>
                                    <input 
                                        type="text"
                                        className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-900 text-sm"
                                        value={editProperty?.zipCode || ''}
                                        readOnly
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-slate-500 mb-1 block">Ville</label>
                                    <input 
                                        type="text"
                                        className="w-full p-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-900 text-sm"
                                        value={editProperty?.city || ''}
                                        readOnly
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-xs font-bold text-slate-500 mb-1 block">Surface (m²)</label>
                                    <input 
                                        type="number"
                                        className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm"
                                    value={editProperty?.surface || ''}
                                    onChange={(e) => editProperty && setEditProperty({...editProperty, surface: Number(e.target.value)})}
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-slate-500 mb-1 block flex items-center"><BedDouble className="w-3 h-3 mr-1"/> Chambres</label>
                                    <input 
                                        type="number"
                                        className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm"
                                    value={editProperty?.bedrooms || ''}
                                    onChange={(e) => editProperty && setEditProperty({...editProperty, bedrooms: Number(e.target.value)})}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-xs font-bold text-slate-500 mb-1 block flex items-center"><Bath className="w-3 h-3 mr-1"/> Salles de bain</label>
                                    <input 
                                        type="number"
                                        className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm"
                                    value={editProperty?.bathrooms || ''}
                                    onChange={(e) => editProperty && setEditProperty({...editProperty, bathrooms: Number(e.target.value)})}
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-slate-500 mb-1 block flex items-center"><Users className="w-3 h-3 mr-1"/> Voyageurs</label>
                                    <input 
                                        type="number"
                                        className="w-full p-2 border border-slate-300 rounded-lg bg-white text-slate-900 text-sm"
                                    value={editProperty?.maxGuests || ''}
                                    onChange={(e) => editProperty && setEditProperty({...editProperty, maxGuests: Number(e.target.value)})}
                                    />
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button 
                                    onClick={saveProperty}
                                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white py-2 rounded-lg text-xs font-bold transition-colors"
                                >
                                    Enregistrer
                                </button>
                                <button 
                                    onClick={() => { setIsEditingProperty(false); setEditProperty(property); }}
                                    className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-2 rounded-lg text-xs font-bold transition-colors"
                                >
                                    Annuler
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-3">
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Adresse</span>
                                    <span className="font-medium text-slate-900 text-right w-1/2 truncate">
                                        {editProperty?.address || 'Non renseigné'}{editProperty?.city ? `, ${editProperty.city}` : ''}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Surface</span>
                                    <span className="font-medium text-slate-900">{editProperty?.surface || 0} m²</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Chambres</span>
                                    <span className="font-medium text-slate-900">{editProperty?.bedrooms || 0}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-slate-500">Capacité</span>
                                    <span className="font-medium text-slate-900">{editProperty?.maxGuests || 0} pers.</span>
                                </div>
                            </div>
                            
                            {editProperty && editProperty.address && (
                                <a 
                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${editProperty.address}, ${editProperty.zipCode || ''} ${editProperty.city || ''}`)}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="mt-4 w-full flex items-center justify-center py-2 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                                >
                                    <MapPin className="w-3.5 h-3.5 mr-2" /> Voir sur la carte
                                </a>
                            )}
                        </>
                    )}
                        </>
                    )}
                </div>

            </div>

            {/* RIGHT MAIN CONTENT */}
            <div className="col-span-8 flex flex-col h-full overflow-hidden">
                
                {/* TABS */}
                <div className="flex border-b border-slate-200 bg-white px-6">
                    <button 
                        onClick={() => setActiveTab('notes')} 
                        className={`py-4 px-4 text-sm font-bold border-b-2 transition-colors ${activeTab === 'notes' ? 'border-amber-500 text-amber-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                        Historique & Notes
                    </button>
                    <button 
                        onClick={() => setActiveTab('reminders')} 
                        className={`py-4 px-4 text-sm font-bold border-b-2 transition-colors flex items-center ${activeTab === 'reminders' ? 'border-amber-500 text-amber-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                        Tâches 
                        <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${activeTab === 'reminders' ? 'bg-amber-100' : 'bg-slate-100'}`}>
                            {deal.reminders?.filter(r => !r.isDone).length || 0}
                        </span>
                    </button>
                    <button 
                        onClick={() => setActiveTab('sequence')} 
                        className={`py-4 px-4 text-sm font-bold border-b-2 transition-colors flex items-center ${activeTab === 'sequence' ? 'border-amber-500 text-amber-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                        Séquence Auto
                        {sequenceSteps.length > 0 && !hasCancelledSteps && (
                            <span className="ml-2 w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                        )}
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-6">
                    
                    {/* TAB: NOTES */}
                    {activeTab === 'notes' && (
                        <div className="space-y-6 max-w-3xl mx-auto">
                            {/* SECTION: NOTES DU LEAD MARKETING */}
                            {marketingLead && (
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
                                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider bg-slate-100 px-3 py-1 rounded-full flex items-center gap-2">
                                            <Briefcase className="w-3 h-3" />
                                            Notes du Lead Marketing
                                        </span>
                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
                                    </div>
                                    
                                    {loadingMarketingLead ? (
                                        <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 text-center text-slate-500">
                                            <Clock className="w-5 h-5 mx-auto mb-2 animate-spin" />
                                            <p className="text-sm">Chargement des notes...</p>
                                        </div>
                                    ) : (
                                        <>
                                            {marketingLead.notes && marketingLead.notes.length > 0 ? (
                                                <div className="space-y-4 relative pl-6 border-l-2 border-blue-200 ml-4">
                                                    {marketingLead.notes
                                                        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                                                        .map((note) => (
                                                            <div key={note.id} className="relative group">
                                                                <div className="absolute -left-[31px] top-4 w-4 h-4 rounded-full bg-white border-2 border-blue-300 group-hover:border-blue-500 transition-colors"></div>
                                                                <div className="bg-blue-50/50 p-5 rounded-xl shadow-sm border border-blue-200 text-sm">
                                                                    <div className="flex justify-between items-start mb-3">
                                                                        <span className="font-bold text-slate-900">{note.authorName || 'Anonyme'}</span>
                                                                        <span className="text-xs text-slate-400 bg-white px-2 py-1 rounded-full">{new Date(note.createdAt).toLocaleString()}</span>
                                                                    </div>
                                                                    <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{note.content}</p>
                                                                </div>
                                                            </div>
                                                        ))}
                                                </div>
                                            ) : (
                                                <div className="bg-blue-50/50 p-4 rounded-xl border border-blue-200 text-center">
                                                    <p className="text-sm text-slate-500 italic">Aucune note dans le lead marketing.</p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                    
                                    {/* SECTION: DOCUMENTS DU LEAD MARKETING */}
                                    {marketingLead && marketingLead.documents && marketingLead.documents.length > 0 && (
                                        <>
                                            <div className="flex items-center gap-2 mt-6 mb-4">
                                                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
                                                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider bg-slate-100 px-3 py-1 rounded-full flex items-center gap-2">
                                                    <FileText className="w-3 h-3" />
                                                    Documents du Lead Marketing
                                                </span>
                                                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
                                            </div>
                                            
                                            <div className="space-y-2">
                                                {marketingLead.documents.map((document) => {
                                                    const getFileIcon = (type: string) => {
                                                        if (type.includes('pdf')) return <FileText className="w-4 h-4 text-red-500" />;
                                                        if (type.includes('image')) return <FileText className="w-4 h-4 text-blue-500" />;
                                                        if (type.includes('audio')) return <FileText className="w-4 h-4 text-purple-500" />;
                                                        if (type.includes('video') || type === 'mp4') return <FileText className="w-4 h-4 text-purple-500" />;
                                                        return <FileText className="w-4 h-4 text-slate-500" />;
                                                    };
                                                    
                                                    const formatFileSize = (bytes?: number) => {
                                                        if (!bytes) return '';
                                                        if (bytes < 1024) return `${bytes} B`;
                                                        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
                                                        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
                                                    };
                                                    
                                                    return (
                                                        <div key={document.id} className="bg-blue-50/50 p-4 rounded-xl border border-blue-200 flex items-center justify-between group hover:bg-blue-100/50 transition-colors">
                                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                                {getFileIcon(document.type)}
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="font-medium text-slate-900 text-sm truncate">{document.name}</p>
                                                                    <div className="flex items-center gap-2 mt-1">
                                                                        <span className="text-xs text-slate-500">{document.type}</span>
                                                                        {document.size && (
                                                                            <>
                                                                                <span className="text-xs text-slate-400">•</span>
                                                                                <span className="text-xs text-slate-500">{formatFileSize(document.size)}</span>
                                                                            </>
                                                                        )}
                                                                        {document.uploadedAt && (
                                                                            <>
                                                                                <span className="text-xs text-slate-400">•</span>
                                                                                <span className="text-xs text-slate-500">{new Date(document.uploadedAt).toLocaleDateString()}</span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <button
                                                                onClick={async () => {
                                                                    try {
                                                                        const url = await storage.getMarketingLeadDocumentUrl(document.path);
                                                                        if (url) {
                                                                            window.open(url, '_blank');
                                                                        } else {
                                                                            toast.error("Impossible de générer l'URL de téléchargement");
                                                                        }
                                                                    } catch (error) {
                                                                        console.error('Erreur lors du téléchargement:', error);
                                                                        toast.error("Erreur lors du téléchargement du document");
                                                                    }
                                                                }}
                                                                className="ml-4 p-2 text-slate-500 hover:text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                                                                title="Télécharger"
                                                            >
                                                                <Download className="w-4 h-4" />
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                            
                            {/* SECTION: NOTES DU DEAL */}
                            <div className={marketingLead ? "mt-8" : ""}>
                                {marketingLead && (
                                    <div className="flex items-center gap-2 mb-4">
                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
                                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider bg-slate-100 px-3 py-1 rounded-full">
                                            Notes du Deal
                                        </span>
                                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-slate-300 to-transparent"></div>
                                    </div>
                                )}
                                
                                <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
                                    <textarea 
                                        className="w-full resize-none outline-none text-sm min-h-[80px] bg-transparent text-slate-900 placeholder-slate-400" 
                                        placeholder="Écrivez une note, un compte-rendu d'appel..." 
                                        value={newNote} 
                                        onChange={e => setNewNote(e.target.value)} 
                                    />
                                    <div className="flex justify-end mt-2 pt-2 border-t border-slate-100">
                                        <button onClick={handleAddNote} disabled={!newNote.trim()} className="bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-bold flex items-center disabled:opacity-50 hover:bg-slate-800 transition-colors">
                                            <Send className="w-3.5 h-3.5 mr-2" /> Envoyer
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="space-y-4 relative pl-6 border-l-2 border-slate-200 ml-4 mt-4">
                                    {sortedNotes.map((note) => (
                                        <div key={note.id} className="relative group">
                                            <div className="absolute -left-[31px] top-4 w-4 h-4 rounded-full bg-white border-2 border-slate-300 group-hover:border-amber-500 transition-colors"></div>
                                            <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200 text-sm">
                                                <div className="flex justify-between items-start mb-3">
                                                    <span className="font-bold text-slate-900">{displayAuthorName(note.authorName)}</span>
                                                    <span className="text-xs text-slate-400 bg-slate-50 px-2 py-1 rounded-full">{new Date(note.createdAt).toLocaleString()}</span>
                                                </div>
                                                <p className="text-slate-700 whitespace-pre-wrap leading-relaxed">{note.content}</p>
                                            </div>
                                        </div>
                                    ))}
                                    {sortedNotes.length === 0 && (
                                        <p className="text-slate-400 italic pl-4">Aucune note pour le moment.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* TAB: REMINDERS */}
                    {activeTab === 'reminders' && (
                        <div className="space-y-6 max-w-3xl mx-auto">
                            <div className="bg-white p-5 rounded-xl shadow-sm border border-slate-200">
                                <h4 className="font-bold text-slate-800 mb-3 text-sm uppercase">Nouveau rappel</h4>
                                <div className="flex flex-col space-y-3">
                                    <input type="text" className="w-full outline-none text-sm p-3 bg-slate-50 text-slate-900 rounded-lg border border-slate-200 focus:border-amber-500 transition-colors" placeholder="Que faut-il faire à cette date ?" value={newReminderText} onChange={e => setNewReminderText(e.target.value)} />
                                    <div className="flex items-center space-x-3">
                                        <div className="relative flex-1">
                                            <Calendar className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
                                            <input 
                                                type="date" 
                                                className="text-sm border border-slate-200 rounded-lg p-2 pl-9 focus:border-amber-500 outline-none bg-white w-full text-slate-700" 
                                                value={newReminderDate} 
                                                onChange={e => setNewReminderDate(e.target.value)} 
                                            />
                                        </div>
                                        <div className="relative w-36">
                                            <input
                                                type="time"
                                                className="text-sm border border-slate-200 rounded-lg p-2 focus:border-amber-500 outline-none bg-white w-full text-slate-700"
                                                value={newReminderTime}
                                                onChange={e => setNewReminderTime(e.target.value)}
                                                placeholder="Heure (optionnel)"
                                                title="Avec une heure : RDV (Google Agenda). Sans heure : tâche (Google Tasks)."
                                            />
                                        </div>
                                        <button onClick={handleAddReminder} disabled={!newReminderText.trim() || !newReminderDate} className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-2 rounded-lg text-sm font-bold flex items-center disabled:opacity-50 transition-colors shadow-sm">
                                            <Plus className="w-4 h-4 mr-2" /> Ajouter
                                        </button>
                                    </div>
                                    <p className="text-xs text-slate-400">Heure renseignée → RDV. Sans heure → tâche.</p>
                                </div>
                            </div>

                            <div className="space-y-3">
                                {sortedReminders.map(reminder => (
                                    <div key={reminder.id} className={`flex items-center p-4 rounded-xl border transition-all group ${reminder.isDone ? 'bg-slate-50 border-slate-100 opacity-60' : 'bg-white border-slate-200 shadow-sm hover:shadow-md hover:border-amber-200'}`}>
                                        <button onClick={() => toggleReminder(reminder.id)} className={`w-6 h-6 rounded border flex items-center justify-center mr-4 transition-colors shrink-0 ${reminder.isDone ? 'bg-green-500 border-green-500 text-white' : 'border-slate-300 hover:border-amber-500 text-transparent hover:text-amber-500'}`}>
                                            <CheckSquare className="w-4 h-4" />
                                        </button>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm font-medium ${reminder.isDone ? 'line-through text-slate-500' : 'text-slate-900'}`}>{reminder.note}</p>
                                            <div className="flex items-center mt-1 space-x-2">
                                                <span className={`text-xs flex items-center font-bold px-2 py-0.5 rounded ${reminder.isDone ? 'bg-slate-100 text-slate-400' : 'bg-amber-50 text-amber-700'}`}>
                                                    <Calendar className="w-3 h-3 mr-1.5" />
                                                    {reminder.time
                                                      ? `${new Date(reminder.date).toLocaleDateString()} ${reminder.time}`
                                                      : new Date(reminder.date).toLocaleDateString()}
                                                </span>
                                                <span className={`text-xs px-2 py-0.5 rounded ${reminder.time ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>
                                                    {reminder.time ? 'RDV' : 'Tâche'}
                                                </span>
                                            </div>
                                        </div>
                                        <button onClick={() => deleteReminder(reminder.id)} className="text-slate-300 hover:text-red-500 p-2 rounded-full hover:bg-red-50 transition-colors opacity-0 group-hover:opacity-100">
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                                {sortedReminders.length === 0 && <p className="text-slate-400 italic text-center py-4">Aucun rappel planifié.</p>}
                            </div>
                        </div>
                    )}

                    {/* TAB: SEQUENCE */}
                    {activeTab === 'sequence' && (
                        <div className="max-w-3xl mx-auto">
                            {hasCancelledSteps && (
                                <div className="bg-slate-100 border-l-4 border-slate-500 p-4 rounded-r shadow-sm flex items-center mb-6">
                                    <Ban className="w-5 h-5 text-slate-500 mr-3" />
                                    <div>
                                        <p className="font-bold text-slate-700">Séquence stoppée</p>
                                        <p className="text-sm text-slate-500">Le deal est clos, les étapes futures ont été annulées.</p>
                                    </div>
                                </div>
                            )}

                            {sequenceSteps.length > 0 ? (
                                <div>
                                    <div className="flex justify-between items-center mb-6">
                                        <h3 className="font-bold text-slate-800 flex items-center text-lg">
                                            <Play className="w-5 h-5 mr-2 text-green-500" /> Séquence en cours
                                        </h3>
                                        {!hasCancelledSteps && (
                                            <button onClick={resetSequence} className="text-xs bg-red-50 text-red-600 hover:bg-red-100 px-3 py-1.5 rounded font-bold transition-colors">
                                                Arrêter la séquence
                                            </button>
                                        )}
                                    </div>
                                    
                                    <div className="space-y-6 relative pb-10">
                                        <div className="absolute left-6 top-4 bottom-4 w-0.5 bg-slate-200"></div>
                                        
                                        {sequenceSteps.map((step, idx) => {
                                            const isDone = step.status === 'SENT' || step.status === 'SKIPPED';
                                            const isCancelled = step.status === 'CANCELLED';
                                            const isNext = !isDone && !isCancelled && sequenceSteps.slice(0, idx).every(s => s.status !== 'PENDING');
                                            
                                            let icon = <Mail className="w-4 h-4" />;
                                            if (step.type === 'WHATSAPP') icon = <MessageCircle className="w-4 h-4" />;
                                            if (step.type === 'CALL') icon = <Phone className="w-4 h-4" />;
                                            if (step.type === 'SMS') icon = <MessageSquare className="w-4 h-4" />;
                                            
                                            return (
                                                <div key={step.id} className={`relative flex items-start ${isDone || isCancelled ? 'opacity-60 grayscale' : ''}`}>
                                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 z-10 border-4 border-slate-50 shadow-sm ${isDone ? 'bg-green-100 text-green-600 border-white' : isCancelled ? 'bg-slate-200 text-slate-500' : isNext ? 'bg-amber-500 text-white shadow-lg shadow-amber-200 scale-110' : 'bg-white border-slate-200 text-slate-400'}`}>
                                                        {isDone ? <CheckCircle2 className="w-6 h-6" /> : isCancelled ? <Ban className="w-5 h-5" /> : icon}
                                                    </div>
                                                    <div className={`ml-6 flex-1 bg-white p-5 rounded-xl border transition-all ${isNext ? 'border-amber-400 shadow-md ring-1 ring-amber-100' : 'border-slate-200'}`}>
                                                        <div className="flex justify-between items-start mb-3">
                                                            <div>
                                                                <div className="flex items-center space-x-3">
                                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${step.type === 'WHATSAPP' ? 'bg-green-100 text-green-700' : step.type === 'EMAIL' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>
                                                                        {step.type}
                                                                    </span>
                                                                    <span className="text-xs font-bold text-slate-400 flex items-center">
                                                                        <Calendar className="w-3 h-3 mr-1" />
                                                                        {new Date(step.date).toLocaleDateString()}
                                                                    </span>
                                                                </div>
                                                                <h4 className={`font-bold mt-2 text-base ${isDone || isCancelled ? 'line-through text-slate-400' : 'text-slate-800'}`}>{step.title}</h4>
                                                            </div>
                                                            {isNext && (
                                                                <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-3 py-1 rounded-full animate-pulse border border-amber-200">
                                                                    À FAIRE
                                                                </span>
                                                            )}
                                                            {isCancelled && (
                                                                <span className="text-[10px] font-bold bg-slate-100 text-slate-500 px-2 py-1 rounded">
                                                                    ANNULÉE
                                                                </span>
                                                            )}
                                                        </div>
                                                        
                                                        {!isDone && !isCancelled && (
                                                            <>
                                                                <div className="text-sm text-slate-600 mb-4 bg-slate-50 p-3 rounded-lg border border-slate-100 italic">
                                                                    "{step.content.substring(0, 120)}..."
                                                                </div>
                                                                <div className="flex space-x-3">
                                                                    <button onClick={() => executeStep(step)} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold py-2.5 rounded-lg flex items-center justify-center transition-colors shadow-lg shadow-slate-200">
                                                                        <Send className="w-4 h-4 mr-2" />
                                                                        {step.type === 'CALL' ? 'Appeler maintenant' : 'Envoyer maintenant'}
                                                                    </button>
                                                                    <button onClick={() => skipStep(step.id)} className="px-4 bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 rounded-lg font-bold text-sm transition-colors" title="Ignorer cette étape">
                                                                        Ignorer
                                                                    </button>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center p-12 bg-white rounded-xl border border-dashed border-slate-300">
                                    <div className="bg-slate-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Zap className="w-8 h-8 text-slate-300" />
                                    </div>
                                    <h3 className="font-bold text-lg text-slate-900 mb-2">Aucune séquence active</h3>
                                    <p className="text-slate-500 mb-8 max-w-sm mx-auto">Lancez une séquence automatique pour relancer ce prospect intelligemment.</p>
                                    
                                    {templates.length === 0 ? (
                                        <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg inline-block">Aucun modèle disponible. Créez-en un dans l'administration.</p>
                                    ) : (
                                        <div className="grid grid-cols-1 gap-3 max-w-md mx-auto">
                                            {templates.map(template => (
                                                <button key={template.id} onClick={() => startSequence(template)} className={`flex items-center p-4 bg-white border border-slate-200 rounded-xl transition-all shadow-sm group text-left hover:scale-[1.02] ${
                                                    template.color === 'purple' ? 'hover:border-purple-500 hover:bg-purple-50' :
                                                    template.color === 'blue' ? 'hover:border-blue-500 hover:bg-blue-50' :
                                                    template.color === 'green' ? 'hover:border-green-500 hover:bg-green-50' :
                                                    template.color === 'amber' ? 'hover:border-amber-500 hover:bg-amber-50' :
                                                    'hover:border-slate-500 hover:bg-slate-50'
                                                }`}>
                                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 group-hover:scale-110 transition-transform text-xl ${
                                                        template.color === 'purple' ? 'bg-purple-100 text-purple-600' :
                                                        template.color === 'blue' ? 'bg-blue-100 text-blue-600' :
                                                        template.color === 'green' ? 'bg-green-100 text-green-600' :
                                                        template.color === 'amber' ? 'bg-amber-100 text-amber-600' :
                                                        'bg-slate-100 text-slate-600'
                                                    }`}>{template.icon}</div>
                                                    <div className="flex-1">
                                                        <p className="font-bold text-slate-900">{template.name}</p>
                                                        <p className="text-xs text-slate-500 mt-0.5">
                                                            {template.description || (template.steps ? template.steps.length + ' etapes' : '0 etapes')}
                                                        </p>
                                                    </div>
                                                    <ChevronRight className={`w-5 h-5 ml-auto text-slate-300 ${
                                                        template.color === 'purple' ? 'group-hover:text-purple-500' :
                                                        template.color === 'blue' ? 'group-hover:text-blue-500' :
                                                        template.color === 'green' ? 'group-hover:text-green-500' :
                                                        template.color === 'amber' ? 'group-hover:text-amber-500' :
                                                        'group-hover:text-slate-500'
                                                    }`} />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>
        </div>
      </div>
    </div>
  );
};
